import * as I from 'immutable';
import moment from 'moment';
import React from 'react';

import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {SOURCE_DETAILS_BY_ID} from 'app/utils/constants';

import {DEFAULT_GRADIENT_STOPS, GradientStops} from './gradientStops';
import {
  ANY_TRUECOLOR_HIGH_RES,
  DataLayerInfo,
  ImageLayerInfo,
  LAYERS,
  LAYER_PACKAGES,
  LayerInfo,
  NONE,
  PLANET_BASEMAP_HIGH_RES_TRUECOLOR,
} from './layers';

export function isLayerKeyHighResTruecolor(layerKey: string | null | undefined) {
  return (
    // even though Planet Basemap is technically high res truecolor, we want to bucket it separately
    (layerKey !== PLANET_BASEMAP_HIGH_RES_TRUECOLOR && layerKey?.endsWith('_high-res-truecolor')) ||
    false
  );
}

/**check if a layerKey is a high-res data layer that should be grouped (eg should NAIP_NDVI be
 * grouped into Vegatation (High Resolution)
) */
export function isLayerKeyHighResData(layerKey: string | null | undefined) {
  if (!layerKey) {
    return false;
  }
  const layer = getLayer(layerKey);
  return (
    // not using isLayerKeyHighResTruecolor bc we also want to omit Planet Basemaps/Mosaics
    !layerKey.endsWith('_high-res-truecolor') && layer.resolutionCategory === 'higher-resolution'
  );
}

/**
 * Returns true if the layerKey is an AIRBUS multispectral layer key (e.g., "AIRBUS-PNEO-MULTISPECTRAL_NDVI")
 *
 * Right now, we only support AIRBUS multispectral layers. If/when we support other multispectral sources,
 * we can remove the AIRBUS prefix and just check for "MULTISPECTRAL" in the layerKey.
 */
export function isMultispectralLayerKey(layerKey: string | null | undefined) {
  if (!layerKey) {
    return false;
  }
  const {sourceId} = parseLayerKey(layerKey);
  return SOURCE_DETAILS_BY_ID[sourceId]?.is_multispectral || false;
}

/**
 * Returns true if the layerKey is a grouped high-res data layer key (e.g., "ANY_NDVI_high-res-data")
 */
export function isLayerKeyGroupedHighResData(layerKey: string | null | undefined): boolean {
  if (!layerKey) {
    return false;
  }
  return layerKey.startsWith('ANY_') && layerKey.endsWith('_high-res-data');
}

/**
 * Creates a grouped layer key for high-res data layers (e.g., "ANY_NDVI_high-res-data" for NDVI layers)
 */
export function makeGroupedHighResDataLayerKey(layerId: string): string {
  return `ANY_${layerId}_high-res-data`;
}

export function getLayerIdFromGroupedHighResDataLayerKey(layerKey: string): string {
  return layerKey.replace('ANY_', '').replace('_high-res-data', '');
}

/**
 * Groups grouped high-res data layerKeys by their layerId
 *  returns a list of grouped layer keys along with a map of grouped keys to their constituent layerKeys
 * e.g. {
 *  groupedKeys: ['ANY_NDVI_high-res-data', 'ANY_NDWIw_high-res-data'],
 *  bundleMap: {
 *    'ANY_NDVI_high-res-data': ['NAIP_NDVI', 'AIRBUS_PLEIADES_NDVI'],
 *    'ANY_NDWIw_high-res-data': ['NAIP_NDWIw', 'AIRBUS_PLEIADES_NDWIw']
 *  }}
 */
export function groupGroupedHighResDataLayers(groupedLayerKeys: string[]): {
  groupedKeys: string[];
  groupedLayerKeysMap: Record<string, string[]>;
} {
  const groupedByLayerId = groupedLayerKeys.reduce(
    (acc, layerKey) => {
      const {layerId} = parseLayerKey(layerKey);
      if (!acc[layerId]) {
        acc[layerId] = [];
      }
      acc[layerId].push(layerKey);
      return acc;
    },
    {} as Record<string, string[]>
  );

  const groupedKeys: string[] = [];
  const groupedLayerKeysMap: Record<string, string[]> = {};

  Object.entries(groupedByLayerId).forEach(([layerId, layerKeys]) => {
    const groupedKey = makeGroupedHighResDataLayerKey(layerId);
    groupedKeys.push(groupedKey);
    groupedLayerKeysMap[groupedKey] = layerKeys;
  });

  return {groupedKeys, groupedLayerKeysMap};
}

/**
 * Checks if the activeLayerKey matches any layerKey in the given grouped high-res data layer.
 */
export function isActiveLayerKeyInBundle(activeLayerKey: string, groupedKey: string): boolean {
  if (!isLayerKeyGroupedHighResData(groupedKey)) {
    return false;
  }

  // Extract layerId from grouped key for comparison (e.g., "ANY_NDVI_high-res-data" -> "NDVI")
  const layerId = getLayerIdFromGroupedHighResDataLayerKey(groupedKey);
  // and layerId from the active layer key
  const {layerId: activeLayerId} = parseLayerKey(activeLayerKey);

  // Check if the active layer's layerId matches the group's layerId and is a high-res data layer
  return layerId === activeLayerId && isLayerKeyHighResData(activeLayerKey);
}

export function isLayerKeyRaw(layerKey: string) {
  return layerKey.endsWith('_raw');
}

export function isLayerPaid(layerKey: string) {
  const {sourceId} = parseLayerKey(layerKey);
  return SOURCE_DETAILS_BY_ID[sourceId]?.paid || false;
}

export function getRawLayerKey(layerKey: string): string | null {
  const layer = getLayer(layerKey);
  if (layer.hasRawLayer) {
    return layerKey + '_raw';
  }
  return null;
}

export function getLayerKeyFromSourceAndLayer(source: string, layer: string): string {
  return source + '_' + layer;
}

export function createLayerKeysFromLayerObject(layers: Record<string, string[]>) {
  return Object.entries(layers)
    .map(([source, listofKeys]) => listofKeys.map((key) => `${source}_${key}`))
    .flat();
}

export function createLayerObjectFromLayerKeys(layerKeys: string[]): Record<string, string[]> {
  const layersObject = layerKeys.reduce((acc, layerKey) => {
    const {sourceId, layerId} = parseLayerKey(layerKey);
    acc[sourceId] = acc[sourceId] ? [...acc[sourceId], layerId] : [layerId];
    return acc;
  }, {});
  return layersObject;
}

export function getPackageLayerKeys(layerKey: string): string[] {
  return LAYER_PACKAGES[layerKey] || [layerKey];
}

export function checkLayerIsProcessingStatus(
  layerKey: string,
  metadata: ApiFeatureCollection['processingConfig']['processingMetadata']
): boolean {
  // For grouped high-res data layers, check if any layer in the group is processing
  if (isLayerKeyGroupedHighResData(layerKey)) {
    // (e.g., "ANY_NDVI_high-res-data" -> "NDVI")
    const layerId = getLayerIdFromGroupedHighResDataLayerKey(layerKey);
    // Check all sources that have this layerId
    return Object.keys(metadata).some((sourceId) => {
      const {processing_status} = (metadata[sourceId] || {})[layerId] || {};
      return processing_status === 'processing';
    });
  }

  // Derive our source and layer keys from layerKey
  const {sourceId, layerId} = parseLayerKey(layerKey);
  // Grab our processing key, if it exists.
  const {processing_status} = (metadata[sourceId] || {})[layerId] || {};
  // Check if status is processing. If complete or null, we aren't processing.
  return processing_status === 'processing';
}

export function checkFeatureCollectionIsProcessingStatus(
  featureCollection: I.ImmutableOf<ApiFeatureCollection>
): boolean {
  const metadata = getProcessingMetadataForFeatureCollection(featureCollection);
  // The approach here is to reduce our metadata object to a boolean: whether we're processing anything
  return Object.keys(metadata).reduce<boolean>((isProcessing, source) => {
    // If we've found a processing layer in a previous loop, stop looking, we've already found something.
    if (isProcessing) {
      return isProcessing;
    }
    // If we haven't found anything yet, look through all the layers for processing things.
    return Object.keys(metadata[source]).reduce<boolean>(
      (isProcessing, layer) =>
        isProcessing || metadata[source][layer].processing_status === 'processing',
      false
    );
  }, false);
}

export function getSupportedLayerKeysForFeatureCollection(
  featureCollection: I.ImmutableOf<ApiFeatureCollection>
) {
  return createLayerKeysFromLayerObject(
    featureCollection.get('supportedLayersForProduct').toJS()
  ).filter((layerKey) => {
    const layer = LAYERS[layerKey];
    if (layer?.filter) {
      return layer.filter(featureCollection);
    } else {
      return true;
    }
  });
}

export function getEnrolledLayerKeysForFeatureCollection(
  featureCollection: I.ImmutableOf<ApiFeatureCollection>
) {
  return createLayerKeysFromLayerObject(
    featureCollection.getIn(['processingConfig', 'enrolledLayers']).toJS()
  );
}

// TODO(anthony): This is a bit unlike the other accessors, above, which use createLayerKeysFromLayerObject.
//  Should we flatten the returned structure to something more like { [layerKey: string]: boolean }?
export function getProcessingMetadataForFeatureCollection(
  featureCollection: I.ImmutableOf<ApiFeatureCollection>
) {
  return featureCollection.getIn(['processingConfig', 'processingMetadata']).toJS();
}

export function getLayerMenuOptionsForFeatureCollection(
  featureCollection: I.ImmutableOf<ApiFeatureCollection>
) {
  const layerKeys = getEnrolledLayerKeysForFeatureCollection(featureCollection);

  // filter out highResTruecolor layerKeys. This function is used for displaying options in the
  // layers dropdown or other contexts where we don't distingush between different highResTruecolor layers.
  // filter out raw layerKeys, we also don't need to show those in the dropdown
  const filteredLayerKeys = layerKeys.filter(
    (layerKey) =>
      !isLayerKeyHighResTruecolor(layerKey) &&
      !isLayerKeyRaw(layerKey) &&
      !isLayerKeyHighResData(layerKey)
  );

  const groupedLayerKeys = layerKeys.filter(isLayerKeyHighResData);
  const {groupedKeys} = groupGroupedHighResDataLayers(groupedLayerKeys);

  return [...filteredLayerKeys, ...groupedKeys, NONE, ANY_TRUECOLOR_HIGH_RES];
}

export function getLayer(layerKey: string | undefined, includeHiRes?: boolean) {
  if (!layerKey) {
    throw 'layerKey cannot be undefined';
  }

  // We don't always have separate data for various truecolor high res sources.
  if (isLayerKeyHighResTruecolor(layerKey) && !includeHiRes) {
    layerKey = ANY_TRUECOLOR_HIGH_RES;
  }

  // since this could be any kind of high-res data layer we make a LayerInfo for it dynamically
  if (isLayerKeyGroupedHighResData(layerKey)) {
    return makeGroupedHighResDataLayerInfo(layerKey);
  }

  if (LAYERS[layerKey]) {
    return LAYERS[layerKey];
  } else {
    return makeDefaultLayer(layerKey);
  }
}

/**
 * Creates a fake LayerInfo for a grouped high-res data layer.
 * Normally we could just add a dummy record to layers.tsx like we do for ANY_high-res-truecolor, but
 * since we may have multiple high-res data layers (eg NDVI and NDWIw) we need to
 * generate these on the fly.
 * Uses the first layer in the group as a template for properties like gradientStops,
 * dataRange, etc., but changes the display name to "LayerName (High Resolution)".
 *
 * TODO(eva): would it make more sense to actually just add dummy records to layers.tsx?
 */
function makeGroupedHighResDataLayerInfo(groupedKey: string): LayerInfo {
  // what layer are we looking at(e.g., "ANY_NDVI_high-res-data" -> "NDVI")
  const layerId = getLayerIdFromGroupedHighResDataLayerKey(groupedKey);

  // Find the first high-res LayerInfo that matches this layerId
  const firstLayerKey = Object.keys(LAYERS).find(
    (key) => parseLayerKey(key).layerId === layerId && isLayerKeyHighResData(key)
  );

  if (firstLayerKey && LAYERS[firstLayerKey]) {
    const baseLayer = LAYERS[firstLayerKey];
    // Get the shortName from the base layer (e.g., "Vegetation" from "Vegetation (NAIP)")
    const shortName = baseLayer.shortName || layerId;

    return {
      ...baseLayer,
      key: groupedKey,
      display: `${shortName} (High Resolution)`,
      shortName: shortName,
    };
  }

  // Fallback if we can't find a matching layer
  return {
    type: 'data',
    key: groupedKey,
    display: `${layerId} (High Resolution)`,
    shortName: layerId,
    vendor: 'none',
    gradientStops: DEFAULT_GRADIENT_STOPS,
    dataRange: [-1, 1],
  };
}

function makeDefaultLayer(layerKey: string): LayerInfo {
  return {
    type: 'image',
    key: layerKey,
    display: layerKey,
    shortName: layerKey,
    vendor: 'none',
    gradientStops: DEFAULT_GRADIENT_STOPS,
  };
}

export function isImageLayer(layerInfo: LayerInfo): layerInfo is ImageLayerInfo {
  return layerInfo.type === 'image';
}

export function isDataLayer(layerInfo: LayerInfo): layerInfo is DataLayerInfo {
  return layerInfo.type === 'data';
}

export function renderGradientStops(stops: GradientStops) {
  return stops.map(([offset, color], i) => (
    <stop key={i} offset={`${offset * 100}%`} stopColor={color} />
  ));
}

// A function to conditionally adjust display dates
// based on the layer.
export function conditionallyAdjustLayerDate(
  date: Date | string | moment.Moment,
  layerKey: string | undefined = ''
): moment.Moment {
  const layer = LAYERS[layerKey];
  // For all mosaic layers, we want to show the date as the last day of the time period.
  if (layer?.mosaic) {
    const [, endDate] = staticLayerMosaicRange(layerKey, date) || [moment(date), moment(date)];
    return endDate;
  }
  return moment(date);
}

// Returns a list of moments representing the constant mosaic range for the layer, or
// null if not applicable.
export function staticLayerMosaicRange(
  layerKey: string | undefined,
  date: Date | string | moment.Moment
): moment.Moment[] | null {
  if (layerKey === undefined) {
    return null;
  }

  const layer = LAYERS[layerKey];
  const momentDate = moment(date).utc();
  if (layer?.mosaic == 'annual') {
    const startDate = moment(momentDate).startOf('year');
    const endDate = moment(momentDate).endOf('year');
    return [startDate, endDate];
  } else if (layer?.mosaic == 'monthly_start') {
    // For monthly_start, the date represents the start of the month (UTC), inclusive
    // e.g. momentDate is 2024-03-01 and the range we display is 2024-03-01 to 2024-03-31.
    const startDate = moment(momentDate).startOf('month');
    const endDate = moment(momentDate).endOf('month');
    return [startDate, endDate];
  } else if (layer?.mosaic == 'monthly_end') {
    // For monthly_end, the date represents the end of the month (UTC), exclusive
    // e.g. momentDate is 2024-04-01 and the range we display is 2024-03-01 to 2024-03-31.
    const startDate = moment(momentDate).subtract(1, 'month').startOf('month');
    const endDate = moment(startDate).endOf('month');
    return [startDate, endDate];
  } else if (typeof layer?.mosaic === 'function') {
    const [startDate, endDate] = layer.mosaic(momentDate.toDate());
    return [moment(startDate), moment(endDate)];
  }

  return null;
}

export function onlyShowCaptureDate(layerKey: string | undefined): boolean {
  if (!layerKey) {
    return false;
  }
  const layer = LAYERS[layerKey];
  return !!layer?.onlyDisplayCaptureDate;
}

export interface LayerKey {
  sourceId: string;
  layerId: string;
}

/**
 * The layerKeys we use throughout the frontend are a composite of two distinct pieces of data:
 * the sourceId and layerId (the backend also sometimes calls the layerId a layerKey).
 * This function parses the combined layerKey into a LayerKey object so each field can be
 * referenced distinctly.
 */
export function parseLayerKey(combinedLayerKey: string): LayerKey {
  const sourceId = combinedLayerKey.split('_', 1)[0];
  const layerId = combinedLayerKey.replace(`${sourceId}_`, '');
  return {
    sourceId,
    layerId,
  };
}

/**
 * Whether a share link may render this layer. Licensing is per imagery source, so the
 * basemap and the generic ANY_ keys (which resolve to a concrete source only against a
 * feature's data, and a share link's data already withholds unshareable sources) pass.
 */
export function isLayerKeyShareable(layerKey: string): boolean {
  if (layerKey === NONE || layerKey.startsWith('ANY_')) {
    return true;
  }
  return SOURCE_DETAILS_BY_ID[parseLayerKey(layerKey).sourceId]?.shareable === true;
}

export function imageRefsAreShareable(imageRefs: ReadonlyArray<{layerKey: string}>): boolean {
  return imageRefs.every((imageRef) => isLayerKeyShareable(imageRef.layerKey));
}

/**
 *
 * Returns the resolution of a layer given its layerKey. If we haven't
 * specified an override on the LAYERS object, default to the source resolution.
 */
export function getLayerResolution(layerKey: string): number {
  const {sourceId} = parseLayerKey(layerKey);
  const layerResolution = getLayer(layerKey)?.resolution;

  return layerResolution || SOURCE_DETAILS_BY_ID[sourceId]?.resolution;
}
